import { UseVenueBasedMembershipGroupService } from "@app-contexts/dashboard/features/memberships/data/services/membership-group/useVenueBasedMembershipGroupService";
import { VenueBasedMembershipGroupSerializer } from "@app-contexts/dashboard/features/memberships/data/types/membership-groups";
import ResponsiveDialog from "@shared/components/ui-kit/overlays/ResponsiveDialog/ResponsiveDialog.web";
import { enums, PAYMENT_TYPE_OPTIONS_MAP } from "@shared/constants";
import { useEmbeddedComponent } from "@shared/hooks/useEmbeddedComponent";
import { TicketTypeSerializer } from "@shared/modules/events/types/public";
import { getEmbeddedPurchasePlatform } from "@shared/modules/financials/utils/purchase-platform-utils";
import MembershipTypesList from "@shared/modules/memberships/components/MembershipTypesList/MembershipTypesList";
import { UsePublicMembershipGroupsService } from "@shared/modules/memberships/data/services/usePublicMembershipGroupsService";
import { PublicMembershipLevelSerializer } from "@shared/modules/memberships/types";
import { isPublicMembershipGroup } from "@shared/modules/memberships/utils/helpers";
import { BestAvailableSeatingQueryParamsActionType } from "@shared/modules/seating/types/seating-types";
import { useUserVenueSession } from "@shared/modules/venue/hooks/useUserVenueSession";
import { useTranslation } from "@shared/utils/internationalization";
import Button from "@ui-kit/form/Button/Button.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { Dispatch } from "react";
import { BestAvailableQueryActions } from "../../hooks/useBestAvailableQueryParams";
import {
  BestAvailableTypeActions,
  useBestAvailableTypeState,
} from "./hooks/useBestAvailableTypeState";

type BestAvailableMembershipLevelSelectionDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  updateBestAvailableQueryParams: Dispatch<BestAvailableSeatingQueryParamsActionType>;
  membershipService: () =>
    | UseVenueBasedMembershipGroupService
    | UsePublicMembershipGroupsService;
  membershipGroupSlug: string;
  initialSelectedMembershipLevels: Record<string, number>;
  typesOnMap: Array<number>;
};

export function BestAvailableMembershipLevelSelectionDialog({
  isOpen,
  onClose,
  updateBestAvailableQueryParams,
  membershipService,
  membershipGroupSlug,
  initialSelectedMembershipLevels,
  typesOnMap,
}: BestAvailableMembershipLevelSelectionDialogProps) {
  const { t } = useTranslation("seating");
  const { bestAvailableTypeState, dispatch } = useBestAvailableTypeState(
    initialSelectedMembershipLevels
  );
  const { isEmbedded } = useEmbeddedComponent();

  const membershipGroupParams = {
    // if on the seating widget, use widget logic for the purchase platform
    purchase_platform: isEmbedded
      ? getEmbeddedPurchasePlatform()
      : enums.PURCHASE_SOURCE_PLATFORM_MAP.PLATFORM_WEB.id,
    payment_type: [
      PAYMENT_TYPE_OPTIONS_MAP.CREDIT.id,
      PAYMENT_TYPE_OPTIONS_MAP.AFTERPAY.id,
      PAYMENT_TYPE_OPTIONS_MAP.AFFIRM.id,
    ].toString(),
  };

  const { useRetrieve } = membershipService();
  const { data: membershipGroupData, isLoading: isLoadingMembershipGroup } = (
    useRetrieve as any
  )(membershipGroupSlug, {
    config: { params: membershipGroupParams },
    options: {
      enabled: Boolean(
        typeof window !== "undefined" &&
          typeof document !== "undefined" &&
          membershipGroupSlug &&
          membershipGroupParams
      ),
    },
  });

  const { data: currentVenue } = useUserVenueSession({
    options: {
      enabled:
        !isLoadingMembershipGroup &&
        membershipGroupData &&
        !isPublicMembershipGroup(membershipGroupData),
    },
  });

  const handleClose = () => {
    updateBestAvailableQueryParams({
      type: BestAvailableQueryActions.MEMBERSHIP_LEVELS,
      payload: { membership_levels: bestAvailableTypeState },
    });
    onClose();
  };

  const handleQuantityChange = (tt: TicketTypeSerializer, quantity: number) => {
    const { id } = tt;

    dispatch({
      type:
        quantity === 0
          ? BestAvailableTypeActions.REMOVE_TYPE
          : BestAvailableTypeActions.SET_TYPE,
      payload: { id, quantity },
    });
  };

  const venue =
    membershipGroupData && isPublicMembershipGroup(membershipGroupData)
      ? membershipGroupData?.venue
      : currentVenue;

  const membershipLevels =
    membershipGroupData?.membership_levels &&
    Array.isArray(membershipGroupData?.membership_levels)
      ? // type gymnastics to get typescript to work
        (
          membershipGroupData.membership_levels as Array<
            | PublicMembershipLevelSerializer
            | VenueBasedMembershipGroupSerializer["membership_levels"][0]
          >
        ).filter((ml) => typesOnMap.includes(ml.id))
      : undefined;

  return (
    <ResponsiveDialog
      isOpen={isOpen}
      onClose={handleClose}
      isCentered
      size="2xl"
      headerComponent={<Text>{t("Memberships")}</Text>}
      variant="interactive"
      scrollBehavior="inside"
      drawerContentProps={{
        height: "100dvh",
      }}
      bodyComponent={
        membershipGroupData && venue && membershipLevels && venue.currency ? (
          <MembershipTypesList
            membershipGroup={membershipGroupData}
            membershipLevels={membershipLevels}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            venue={venue as any}
            currency={venue.currency}
            hasAllInPricing={venue.all_in_pricing_is_enabled ?? false}
            isLoading={isLoadingMembershipGroup}
            onAmountRequested={handleQuantityChange}
            forceQuantityButtons
            withDebounce={false}
            initialSelectedMembershipLevels={bestAvailableTypeState}
            enableTracking={false}
          />
        ) : null
      }
      footerComponent={
        <Button.Solid
          onClick={handleClose}
          width={{ base: "full", sm: "auto" }}
          data-testid="best-available-membership-level-selection-dialog-continue-button"
        >
          {t("Continue")}
        </Button.Solid>
      }
    />
  );
}
